import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';
import { useMutation } from '@tanstack/react-query';
import {
  AlertTriangle,
  CheckCircle,
  Database,
  Download,
  Eye,
  Loader2,
  Play,
  Settings,
  XCircle,
} from 'lucide-react';
import { useState } from 'react';

interface SchemaComparison {
  tables: {
    added: string[];
    removed: string[];
    modified: any[];
    unchanged: string[];
  };
  columns: {
    added: any[];
    removed: any[];
    modified: any[];
    unchanged: any[];
  };
  types: {
    added: string[];
    removed: string[];
    modified: any[];
    unchanged: string[];
  };
  indexes: {
    added: any[];
    removed: any[];
    unchanged: any[];
  };
  migrationSteps: any[];
}

interface MigrationStep {
  type: string;
  table?: string;
  column?: string;
  enum?: string;
  value?: string;
  sql: string;
  description: string;
  safe: boolean;
  warning?: string;
}

interface MigrationResult {
  success: boolean;
  message: string;
  migrationResults: string[];
  migrationErrors: string[];
  safetyBackup: string;
  timestamp: string;
}

export default function SchemaComparison() {
  const { toast } = useToast();
  const [uploadedSchema, setUploadedSchema] = useState<any>(null);
  const [comparison, setComparison] = useState<SchemaComparison | null>(null);
  const [isComparisonDialogOpen, setIsComparisonDialogOpen] = useState(false);
  const [isMigrationDialogOpen, setIsMigrationDialogOpen] = useState(false);
  const [selectedSteps, setSelectedSteps] = useState<MigrationStep[]>([]);
  const [migrationProgress, setMigrationProgress] = useState<string[]>([]);
  const [isMigrationRunning, setIsMigrationRunning] = useState(false);

  // Export schema mutation
  const exportMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest('GET', '/api/admin/schema/export');
      return response.json();
    },
    onSuccess: data => {
      // Create download link
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `staffboard_schema_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast({
        title: 'Schema Exported Successfully',
        description: `Schema exported with ${Object.keys(data.schema.tables).length} tables`,
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Export Failed',
        description: error.message || 'Failed to export schema',
        variant: 'destructive',
      });
    },
  });

  // Upload schema file mutation
  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('schemaFile', file);

      const response = await apiRequest(
        'POST',
        '/api/admin/schema/upload-schema',
        formData
      );
      return response.json();
    },
    onSuccess: data => {
      setUploadedSchema(data.schema);
      toast({
        title: 'Schema Uploaded Successfully',
        description: `Schema uploaded with ${Object.keys(data.schema.tables).length} tables`,
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Upload Failed',
        description: error.message || 'Failed to upload schema file',
        variant: 'destructive',
      });
    },
  });

  // Compare schemas mutation
  const compareMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest('POST', '/api/admin/schema/compare', {
        uploadedSchema,
      });
      return response.json();
    },
    onSuccess: data => {
      setComparison(data.comparison);
      setIsComparisonDialogOpen(true);
      toast({
        title: 'Schema Comparison Complete',
        description: `Found ${data.comparison.migrationSteps.length} migration steps`,
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Comparison Failed',
        description: error.message || 'Failed to compare schemas',
        variant: 'destructive',
      });
    },
  });

  // Execute migration mutation
  const migrationMutation = useMutation<MigrationResult>({
    mutationFn: async () => {
      const response = await apiRequest('POST', '/api/admin/schema/migrate', {
        migrationSteps: selectedSteps,
        preserveData: true,
      });
      return response.json();
    },
    onSuccess: data => {
      setIsMigrationRunning(false);
      setMigrationProgress(data.migrationResults);

      const successCount = data.migrationResults.filter(r =>
        r.startsWith('✅')
      ).length;
      const errorCount = data.migrationErrors.length;

      toast({
        title: 'Schema Migration Completed',
        description: `✅ ${successCount} successful, ${errorCount > 0 ? `❌ ${errorCount} errors` : 'no errors'}. Backup: ${data.safetyBackup}`,
      });

      setIsMigrationDialogOpen(false);
      setSelectedSteps([]);
    },
    onError: (error: any) => {
      setIsMigrationRunning(false);
      toast({
        title: 'Migration Failed',
        description: error.message || 'Failed to migrate schema',
        variant: 'destructive',
      });
    },
  });

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      uploadMutation.mutate(file);
    }
  };

  const handleCompare = () => {
    if (uploadedSchema) {
      compareMutation.mutate();
    }
  };

  const handleMigration = () => {
    if (selectedSteps.length > 0) {
      setIsMigrationRunning(true);
      migrationMutation.mutate();
    }
  };

  const toggleStepSelection = (step: MigrationStep) => {
    setSelectedSteps(prev => {
      const isSelected = prev.some(s => s.sql === step.sql);
      if (isSelected) {
        return prev.filter(s => s.sql !== step.sql);
      } else {
        return [...prev, step];
      }
    });
  };

  const getStepIcon = (step: MigrationStep) => {
    if (step.type === 'warning')
      return <AlertTriangle className="h-4 w-4 text-amber-600" />;
    if (step.safe) return <CheckCircle className="h-4 w-4 text-green-600" />;
    return <XCircle className="h-4 w-4 text-red-600" />;
  };

  const getStepBadge = (step: MigrationStep) => {
    if (step.type === 'warning')
      return (
        <Badge variant="outline" className="border-amber-500 text-amber-700">
          Warning
        </Badge>
      );
    if (step.safe)
      return (
        <Badge variant="default" className="bg-green-100 text-green-800">
          Safe
        </Badge>
      );
    return <Badge variant="destructive">Risky</Badge>;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Database className="h-5 w-5" />
          Schema Comparison & Migration
        </CardTitle>
        <CardDescription>
          Export schema from new PC, upload to old PC, and compare schemas to
          migrate table structure while preserving data
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Export Section */}
        <div className="p-4 bg-blue-50 dark:bg-blue-950 rounded-md">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium text-blue-800 dark:text-blue-200">
                Step 1: Export Schema from New PC
              </h3>
              <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                Click the button below to download the current database schema
                as a JSON file
              </p>
            </div>
            <Button
              onClick={() => exportMutation.mutate()}
              disabled={exportMutation.isPending}
              className="flex items-center gap-2"
            >
              {exportMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Exporting...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4" />
                  Export Schema
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Upload Section */}
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium mb-2">
                Step 2: Upload Schema File to Old PC
              </label>
              <input
                type="file"
                accept=".json"
                onChange={handleFileUpload}
                disabled={uploadMutation.isPending}
                className="w-full p-2 border rounded-md"
              />
              <p className="text-xs text-gray-500 mt-1">
                Select the exported JSON schema file from your new PC
              </p>
            </div>
            <Button
              onClick={handleCompare}
              disabled={!uploadedSchema || compareMutation.isPending}
              className="flex items-center gap-2"
            >
              {compareMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Comparing...
                </>
              ) : (
                <>
                  <Eye className="h-4 w-4" />
                  Compare Schemas
                </>
              )}
            </Button>
          </div>

          {uploadedSchema && (
            <div className="p-3 bg-green-50 dark:bg-green-950 rounded-md">
              <div className="flex items-center gap-2 text-green-800 dark:text-green-200">
                <CheckCircle className="h-4 w-4" />
                <span className="font-medium">
                  Schema Uploaded Successfully
                </span>
              </div>
              <div className="mt-2 text-sm text-green-700 dark:text-green-300">
                <p>Tables: {Object.keys(uploadedSchema.tables).length}</p>
                <p>Types: {Object.keys(uploadedSchema.types).length}</p>
                <p>Indexes: {uploadedSchema.indexes.length}</p>
              </div>
            </div>
          )}
        </div>

        {/* Comparison Results Dialog */}
        <Dialog
          open={isComparisonDialogOpen}
          onOpenChange={setIsComparisonDialogOpen}
        >
          <DialogContent className="max-w-6xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Database className="h-5 w-5" />
                Schema Comparison Results
              </DialogTitle>
              <DialogDescription>
                Review the differences between your current schema and the
                uploaded schema
              </DialogDescription>
            </DialogHeader>

            {comparison && (
              <div className="space-y-6">
                {/* Summary */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">
                      {comparison.tables.added.length +
                        comparison.types.added.length}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      New Items
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">
                      {comparison.tables.modified.length +
                        comparison.types.modified.length}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      Modified
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-amber-600">
                      {comparison.tables.removed.length +
                        comparison.types.removed.length}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      Removed
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-600">
                      {comparison.tables.unchanged.length +
                        comparison.types.unchanged.length}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      Unchanged
                    </div>
                  </div>
                </div>

                {/* Migration Steps */}
                <div className="space-y-3">
                  <h3 className="font-semibold flex items-center gap-2">
                    <Settings className="h-4 w-4" />
                    Migration Steps ({comparison.migrationSteps.length})
                  </h3>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {comparison.migrationSteps.map((step, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 bg-white dark:bg-gray-700 rounded border"
                      >
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={selectedSteps.some(
                              s => s.sql === step.sql
                            )}
                            onChange={() => toggleStepSelection(step)}
                            disabled={step.type === 'warning'}
                            className="rounded"
                          />
                          <div className="flex items-center gap-2">
                            {getStepIcon(step)}
                            <span className="text-sm font-medium">
                              {step.description}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {step.warning && (
                            <span className="text-xs text-amber-600">
                              {step.warning}
                            </span>
                          )}
                          {getStepBadge(step)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Selected Steps Summary */}
                {selectedSteps.length > 0 && (
                  <div className="p-3 bg-blue-50 dark:bg-blue-950 rounded-md">
                    <div className="flex items-center gap-2 text-blue-800 dark:text-blue-200">
                      <Play className="h-4 w-4" />
                      <span className="font-medium">
                        {selectedSteps.length} steps selected for migration
                      </span>
                    </div>
                    <div className="mt-2 text-sm text-blue-700 dark:text-blue-300">
                      <p>
                        Safe operations:{' '}
                        {selectedSteps.filter(s => s.safe).length}
                      </p>
                      <p>
                        Risky operations:{' '}
                        {selectedSteps.filter(s => !s.safe).length}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setIsComparisonDialogOpen(false)}
              >
                Close
              </Button>
              <Button
                onClick={() => {
                  setIsComparisonDialogOpen(false);
                  setIsMigrationDialogOpen(true);
                }}
                disabled={selectedSteps.length === 0}
                className="flex items-center gap-2"
              >
                <Settings className="h-4 w-4" />
                Execute Migration ({selectedSteps.length})
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Migration Confirmation Dialog */}
        <Dialog
          open={isMigrationDialogOpen}
          onOpenChange={setIsMigrationDialogOpen}
        >
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Database className="h-5 w-5" />
                Confirm Schema Migration
              </DialogTitle>
              <DialogDescription>
                Review the selected migration steps before executing. A safety
                backup will be created automatically.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {/* Safety Warning */}
              <div className="p-4 bg-amber-50 dark:bg-amber-950 rounded-md">
                <div className="flex items-center gap-2 text-amber-800 dark:text-amber-200">
                  <AlertTriangle className="h-4 w-4" />
                  <span className="font-medium">
                    Important Safety Information
                  </span>
                </div>
                <div className="mt-2 text-sm text-amber-700 dark:text-amber-300">
                  <ul className="list-disc list-inside space-y-1">
                    <li>A safety backup will be created before any changes</li>
                    <li>All existing data will be preserved</li>
                    <li>
                      Schema changes cannot be undone without restoring from
                      backup
                    </li>
                    <li>
                      Test the migration in a development environment first
                    </li>
                  </ul>
                </div>
              </div>

              {/* Selected Steps */}
              <div className="space-y-3">
                <h3 className="font-semibold">Selected Migration Steps:</h3>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {selectedSteps.map((step, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-3 p-2 bg-gray-50 dark:bg-gray-800 rounded"
                    >
                      {getStepIcon(step)}
                      <span className="text-sm">{step.description}</span>
                      {getStepBadge(step)}
                    </div>
                  ))}
                </div>
              </div>

              {/* Migration Progress */}
              {migrationProgress.length > 0 && (
                <div className="space-y-3">
                  <h3 className="font-semibold">Migration Progress:</h3>
                  <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-sm max-h-40 overflow-y-auto">
                    {migrationProgress.map((result, index) => (
                      <div key={index} className="mb-1">
                        {result}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setIsMigrationDialogOpen(false)}
                disabled={isMigrationRunning}
              >
                Cancel
              </Button>
              <Button
                onClick={handleMigration}
                disabled={isMigrationRunning || selectedSteps.length === 0}
                className="flex items-center gap-2"
              >
                {isMigrationRunning ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Migrating...
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4" />
                    Execute Migration
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}
